import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { useAppContext } from 'providers/AppProvider';
import { TooltipComponent } from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { tooltipFormatterDefault } from 'helpers/echart-utils';
import { fetchHVACallsPerHourandCampaignInbound } from 'services/hvacallingmanagment.service';
import { addHours } from 'date-fns';

echarts.use([TooltipComponent, LineChart]);

interface ChartDataItem {
  campaignid: number;
  startdatetime: string;
  handled: number;
  offered: number;
}

interface DialingPerformanceChartProps {
  campaignid?: string;
}

interface ProcessedData {
  hours: string[];
  seriesData: {
    offered: number[];
    handled: number[];
  };
}

const DialingPerformanceChartInbound: React.FC<
  DialingPerformanceChartProps
> = ({ campaignid }) => {
  // eslint-disable-next-line
  const { config: { isDark }, getThemeColor, addAlphaToHexColor } = useAppContext();
  const chartRef = useRef<ReactEChartsCore>(null);
  const [zoomState, setZoomState] = useState({ start: 0, end: 100 });
  const [chartData, setChartData] = useState<ProcessedData>({
    hours: [],
    seriesData: { offered: [], handled: [] }
  });

  const getDefaultOptions = (
    xAxisData: string[],
    seriesData: { offered: number[]; handled: number[] },
    currentZoomState: { start: number; end: number },
    themeGetter: (name: string) => string,
    darkMode: boolean
  ) => ({
    color: [themeGetter('primary'), themeGetter('success-light')],
    tooltip: {
      trigger: 'axis',
      // eslint-disable-next-line
      formatter: (params: any) =>
        tooltipFormatterDefault(params, 'MMM DD, YYYY', 'color'),
      // eslint-disable-next-line
      backgroundColor: !darkMode ? 'rgba(240, 240, 240, 0.9)' : 'rgba(49, 55, 74, 0.9)',
      textStyle: {
        color: !darkMode ? '#202020' : '#ccc',
        fontFamily: 'Nunito Sans',
        fontSize: 14
      },
      axisPointer: {
        shadowStyle: {
          color: 'red'
        }
      },
      // eslint-disable-next-line
      borderColor: !darkMode ? 'rgba(49, 55, 74, 0.4)' : 'rgba(140, 140, 180, 0.8)'
    },
    legend: {
      top: '0',
      data: [
        {
          name: 'Offered',
          icon: 'roundRect'
        },
        {
          name: 'Handled',
          icon: 'roundRect'
        }
      ],
      itemWidth: 16,
      itemHeight: 8,
      itemGap: 10,
      inactiveColor: themeGetter('quaternary-color'),
      inactiveBorderWidth: 0,
      textStyle: {
        color: themeGetter('body-color'),
        fontWeight: 500,
        fontSize: 12,
        fontFamily: 'Nunito Sans'
      }
    },
    toolbox: {
      itemSize: 10,
      showTitle: false,
      show: true,
      iconStyle: {
        // eslint-disable-next-line
        borderColor: !isDark ? 'rgba(160, 160, 190, 0.9)' : 'rgba(100, 100, 150, 0.9)'
      },
      emphasis: {
        iconStyle: { borderColor: getThemeColor('primary') }
      },
      feature: {
        mark: { show: false },
        magicType: { show: false, type: ['line', 'bar'] },
        dataZoom: { show: false },
        dataView: { show: false, readOnly: true },
        restore: { show: false },
        saveAsImage: { show: true }
      }
    },
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: [0, 1],
        start: currentZoomState.start,
        end: currentZoomState.end,
        filterMode: 'filter',
        zoomOnMouseWheel: false,
        moveOnMouseWheel: false,
        moveOnMouseMove: true
      },
      {
        show: false,
        xAxisIndex: [0, 1],
        type: 'slider',
        left: '10',
        right: '10',
        top: '266',
        height: '20',
        start: currentZoomState.start,
        end: currentZoomState.end,
        fillerColor: addAlphaToHexColor(getThemeColor('primary'), 0.1),
        borderColor: addAlphaToHexColor(getThemeColor('primary'), 0.1),
        handleStyle: {
          color: getThemeColor('primary-light'),
          borderColor: getThemeColor('primary-light')
        },
        textStyle: {
          color: 'rgba(0, 0, 0, 0)',
          fontSize: 10
        },
        brushSelect: false
      }
    ],
    grid: {
      right: 25,
      left: 0,
      bottom: '26',
      top: 30,
      containLabel: true
    },
    xAxis: [
      {
        show: true,
        interval: 2,
        axisLine: {
          lineStyle: {
            type: 'solid',
            color: themeGetter('tertiary-bg')
          }
        },
        axisLabel: {
          color: themeGetter('body-color'),
          interval: 5,
          align: 'left',
          margin: 10,
          fontSize: 8.8
        },
        axisTick: {
          show: true,
          length: 5
        },
        splitLine: {
          interval: 0,
          show: true,
          lineStyle: {
            color: themeGetter('tertiary-bg'),
            type: 'solid'
          }
        },
        type: 'category',
        boundaryGap: false,
        data: xAxisData
      },
      {
        show: true,
        interval: 2,
        axisLine: {
          lineStyle: {
            type: 'solid',
            color: themeGetter('tertiary-bg')
          }
        },
        axisLabel: {
          color: themeGetter('body-color'),
          interval: 5,
          align: 'left',
          margin: 5,
          fontSize: 8.8
        },
        axisTick: {
          show: false
        },
        splitLine: {
          interval: 0,
          show: true,
          lineStyle: {
            color: themeGetter('tertiary-bg'),
            type: 'solid'
          }
        },
        type: 'category',
        boundaryGap: false,
        data: xAxisData
      }
    ],
    yAxis: {
      show: true,
      type: 'value',
      axisLine: {
        lineStyle: {
          type: 'solid',
          color: themeGetter('tertiary-bg')
        }
      },
      axisLabel: {
        color: themeGetter('body-color'),
        margin: 10,
        fontSize: 10.8,
        interval: 0
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: themeGetter('tertiary-bg'),
          type: 'solid'
        }
      },
      axisTick: {
        show: true,
        length: 5,
        alignWithLabel: true,
        lineStyle: {
          color: themeGetter('tertiary-bg')
        }
      }
    },
    series: [
      {
        name: 'Offered',
        type: 'line',
        symbolSize: 6,
        data: seriesData.offered
      },
      {
        name: 'Handled',
        type: 'line',
        symbolSize: 6,
        data: seriesData.handled
      }
    ]
  });

  const processData = (data: ChartDataItem[]): ProcessedData => {
    // Convertir las fechas a la hora local utilizando addHours y TimezoneOffset
    const localData = data.map(item => {
      const utcDate = new Date(item.startdatetime);
      const localDate = addHours(utcDate, utcDate.getTimezoneOffset() / -60);
      return {
        ...item,
        startdatetime: localDate.toISOString()
      };
    });

    // Filtrar datos para el día de hoy
    const today = new Date();
    const filteredData = localData.filter(item => {
      const itemDate = new Date(item.startdatetime);
      return (
        itemDate.getFullYear() === today.getFullYear() &&
        itemDate.getMonth() === today.getMonth() &&
        itemDate.getDate() === today.getDate()
      );
    });

    // Inicializar las estructuras de datos
    // eslint-disable-next-line
    const hours = Array.from({ length: 25 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
    const dialingData = Array(24).fill(0);
    const answerData = Array(24).fill(0);

    // Sumar valores de cada categoría por hora
    filteredData.forEach(item => {
      const localHour = new Date(item.startdatetime).getHours();
      dialingData[localHour] += item.offered;
      answerData[localHour] += item.handled;
    });

    return { hours, seriesData: { offered: dialingData, handled: answerData } };
  };

  const fetchData = useCallback(async () => {
    try {
      const data = await fetchHVACallsPerHourandCampaignInbound({
        campaign: campaignid
      });
      const processedData = processData(data);
      setChartData(processedData);
    } catch (error) {
      console.error('Failed to fetch HVA today`s calls:', error);
    }
  }, [campaignid]);

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, 10000); // Actualiza cada 10 segundos
    return () => clearInterval(intervalId);
  }, [fetchData]);

  useEffect(() => {
    if (chartRef.current) {
      const chart = chartRef.current.getEchartsInstance();

      // Obtener las opciones actuales del gráfico, incluido el estado de zoom
      const currentOptions = chart.getOption();
      const dataZoom =
        Array.isArray(currentOptions?.dataZoom) &&
        currentOptions.dataZoom.length > 0
          ? (currentOptions.dataZoom[0] as { start: number; end: number })
          : zoomState;

      // Actualizar solo las series de datos, sin modificar el zoom ni otras configuraciones
      chart.setOption({
        series: [
          {
            name: 'Offered',
            data: chartData.seriesData.offered
          },
          {
            name: 'Handled',
            data: chartData.seriesData.handled
          }
        ],
        dataZoom: [
          {
            ...dataZoom // Mantener el estado de zoom actual sin cambios
          }
        ]
      });

      // Ajustar el tamaño del gráfico si es necesario
      chart.resize();

      // Guardar el estado del zoom cuando el usuario lo modifique
      chart.off('dataZoom');
      // eslint-disable-next-line
      chart.on('dataZoom', (params: any) => {
        const newZoomState = {
          start: params?.start ?? dataZoom.start,
          end: params?.end ?? dataZoom.end
        };
        setZoomState(newZoomState); // Guardar el nuevo estado del zoom
      });
    }
  }, [chartData]); // Dependemos de chartData únicamente para evitar renderizados innecesarios

  return (
    <ReactEChartsCore
      ref={chartRef}
      echarts={echarts}
      option={getDefaultOptions(
        chartData.hours,
        chartData.seriesData,
        zoomState,
        getThemeColor,
        isDark
      )}
      style={{ height: '245px', width: '100%' }}
    />
  );
};

export default DialingPerformanceChartInbound;
